import { useParams, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  ArrowLeft,
  Phone,
  GraduationCap,
  Calendar,
  Clock,
  Video,
  Plus,
  MapPin,
} from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { format } from "date-fns"
import {
  useGetDoctorById,
  useUpdateDoctorSchedule,
} from "@/services/query/doctors.query"
import { Skeleton } from "@/components/ui/skeleton"
import { AddScheduleDialog } from "@/components/doctors/AddScheduleDialog"
import { AddMediaDialog } from "@/components/doctors/AddMediaDialog"
import { useState } from "react"
import { toast } from "sonner"
import { ScheduleDisplay } from "@/components/doctors/ScheduleDisplay"
import { EditDoctorDescriptionDialog } from "@/components/doctors/EditDoctorDescriptionDialog"
import ReactMarkdown from "react-markdown"

const formatDate = (dateString) => {
  if (!dateString) return "N/A"
  try {
    return format(new Date(dateString), "PPP")
  } catch (error) {
    console.error("Invalid date:", error)
    return "Invalid date"
  }
}

function DoctorDetailsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: doctor, isLoading, isError, error } = useGetDoctorById(id)
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false)
  const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false)
  const [isDescriptionDialogOpen, setIsDescriptionDialogOpen] = useState(false)
  const updateScheduleMutation = useUpdateDoctorSchedule(id)

  const handleAddSchedule = async (scheduleData) => {
    try {
      await updateScheduleMutation.mutateAsync(scheduleData)
      toast.success("Schedule updated successfully")
      setIsScheduleDialogOpen(false)
    } catch (error) {
      toast.error("Failed to update schedule")
    }
  }

  if (!id) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center text-red-500">Invalid doctor ID</div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <Button
          variant="ghost"
          className="mb-6 hover:bg-blue-50"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Doctors
        </Button>
        <DoctorDetailsSkeleton />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="container mx-auto py-6">
        <Button
          variant="ghost"
          className="mb-6 hover:bg-blue-50"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Doctors
        </Button>
        <div className="text-center text-red-500">
          Error loading doctor details: {error?.message || "Unknown error"}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <Button
          variant="ghost"
          className="hover:bg-blue-50 w-fit mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Doctors
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 sm:gap-6 lg:gap-8 lg:min-h-[calc(100vh-8rem)]">
          {/* Profile Section - Now matches tabs height on desktop */}
          <Card className="lg:col-span-2 lg:h-[calc(100vh-5.5rem)] overflow-auto">
            <CardHeader className="sticky top-0 bg-white dark:bg-gray-900 z-10 pb-4 border-b">
              <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg transform transition-transform hover:scale-105">
                <span className="text-2xl sm:text-3xl font-bold text-white">
                  {doctor?.name
                    ?.split(" ")
                    .map((n) => n[0])
                    .slice(0, 2)
                    .join("")}
                </span>
              </div>
              <CardTitle className="text-center text-xl sm:text-2xl">
                {doctor?.name}
              </CardTitle>
              <CardDescription className="text-center">
                <Badge
                  variant="secondary"
                  className="mt-2 text-sm sm:text-base"
                >
                  {doctor?.designation}
                </Badge>
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center text-sm sm:text-base">
                <GraduationCap className="h-4 w-4 mr-2 text-blue-500" />
                <span>{doctor?.qualifications}</span>
              </div>
              <div className="flex items-center text-sm sm:text-base">
                <Phone className="h-4 w-4 mr-2 text-blue-500" />
                <span>{doctor?.phone || "No phone number available"}</span>
              </div>
              <div className="flex items-center text-sm sm:text-base">
                <MapPin className="h-4 w-4 mr-2 text-blue-500" />
                <span>{doctor?.location || "No location available"}</span>
              </div>
              <div className="flex items-center text-sm sm:text-base">
                <Clock className="h-4 w-4 mr-2 text-blue-500" />
                <span>Last updated: {formatDate(doctor?.modified_at)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Tabs Section - Fixed height container */}
          <div className="lg:col-span-4 lg:h-[calc(100vh-8rem)] flex flex-col">
            <Tabs defaultValue="schedule" className="flex flex-col h-full">
              <TabsList className="w-full grid grid-cols-2">
                <TabsTrigger value="schedule" className="text-sm sm:text-base">
                  Schedule
                </TabsTrigger>
                <TabsTrigger
                  value="description"
                  className="text-sm sm:text-base"
                >
                  Description
                </TabsTrigger>
              </TabsList>

              <TabsContent value="schedule" className="flex-1 mt-5 h-full">
                <Card className="h-full flex flex-col">
                  <CardHeader className="border-b shrink-0">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div>
                        <CardTitle className="text-lg sm:text-xl lg:text-2xl">
                          Schedule
                        </CardTitle>
                        <CardDescription className="text-sm sm:text-base">
                          View and manage doctor's schedule
                        </CardDescription>
                      </div>
                      <Button
                        onClick={() => setIsScheduleDialogOpen(true)}
                        className="w-full sm:w-auto shrink-0"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Schedule
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-hidden p-0">
                    <ScrollArea className="h-full">
                      <div className="p-6">
                        <ScheduleDisplay schedule={doctor?.schedule} />
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="description" className="flex-1 mt-5 h-full">
                <Card className="h-full flex flex-col">
                  <CardHeader className="border-b shrink-0">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div>
                        <CardTitle className="text-lg sm:text-xl lg:text-2xl">
                          Doctor's Profile
                        </CardTitle>
                        <CardDescription className="text-sm sm:text-base">
                          Detailed information about the doctor
                        </CardDescription>
                      </div>
                      <Button
                        onClick={() => setIsDescriptionDialogOpen(true)}
                        className="w-full sm:w-auto shrink-0"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Edit Description
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-hidden p-0">
                    <ScrollArea className="h-full">
                      <div className="p-6">
                        <div className="prose prose-blue max-w-none dark:prose-invert">
                          <ReactMarkdown>{doctor?.details || ""}</ReactMarkdown>
                        </div>
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <AddScheduleDialog
        open={isScheduleDialogOpen}
        onOpenChange={setIsScheduleDialogOpen}
        onSubmit={handleAddSchedule}
        doctorId={id}
        existingSchedule={doctor?.schedule}
      />
      <AddMediaDialog
        open={isMediaDialogOpen}
        onOpenChange={setIsMediaDialogOpen}
        doctorId={id}
      />
      <EditDoctorDescriptionDialog
        open={isDescriptionDialogOpen}
        onOpenChange={setIsDescriptionDialogOpen}
        doctor={doctor}
      />
    </div>
  )
}

function DoctorDetailsSkeleton() {
  return (
    <div className="container mx-auto py-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1">
          <Skeleton className="h-[400px] w-full rounded-lg" />
        </div>
        <div className="md:col-span-2 space-y-6">
          <Skeleton className="h-[200px] w-full rounded-lg" />
          <Skeleton className="h-[200px] w-full rounded-lg" />
        </div>
      </div>
    </div>
  )
}

export default DoctorDetailsPage
